import clsx from "clsx";

import { ItemTypeFilter, ItemTypeRow, itemStatusMeta } from "./pinnacleMeta";

const filters: { value: ItemTypeFilter; label: string }[] = [
  { value: "todo", label: "To do" },
  { value: "all", label: "All" },
  { value: "Unmapped", label: "Unmapped" },
  { value: "NeedsCheck", label: "Check legacy" },
  { value: "Mapped", label: "Mapped" },
  { value: "Ignored", label: "Ignored" },
];

/** The Pinnacle item types, what needs doing first at the top. */
export function ItemTypeList({
  rows,
  selectedId,
  onSelect,
  filter,
  onFilterChange,
  search,
  onSearchChange,
  showUsage,
}: {
  rows: ItemTypeRow[];
  selectedId?: string;
  onSelect: (id: string) => void;
  filter: ItemTypeFilter;
  onFilterChange: (filter: ItemTypeFilter) => void;
  search: string;
  onSearchChange: (search: string) => void;
  showUsage: boolean;
}) {
  return (
    <div className="flex min-h-0 flex-col">
      <div className="space-y-2 border-b border-gray-200 p-3">
        <input
          id="pinnacle-item-search"
          type="search"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search item types"
          aria-label="Search item types"
          className="w-full rounded border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-600"
        />
        <div className="flex flex-wrap gap-1" role="group" aria-label="Filter item types">
          {filters.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => onFilterChange(f.value)}
              aria-pressed={filter === f.value}
              className={clsx(
                "rounded-md px-2 py-1 text-xs font-medium",
                filter === f.value ? "bg-gp-blue-100 text-gp-blue-700" : "text-gray-500 hover:text-gray-700",
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <ul className="max-h-[70vh] overflow-y-auto">
        {rows.length === 0 && <li className="p-4 text-sm text-gray-500">No item types here.</li>}

        {rows.map(({ itemType, usage }) => {
          const status = itemStatusMeta[itemType.status];

          return (
            <li key={itemType.id}>
              <button
                type="button"
                onClick={() => onSelect(itemType.id)}
                aria-current={itemType.id === selectedId}
                className={clsx(
                  "flex w-full flex-col gap-1 border-b border-gray-100 px-3 py-2.5 text-left hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-orange-600",
                  itemType.id === selectedId && "bg-orange-50 shadow-[inset_3px_0_0_theme(colors.orange.600)]",
                )}
              >
                <span className="flex items-center justify-between gap-2">
                  <span className="truncate rounded bg-yellow-400 px-1.5 py-0.5 font-mono text-xs font-medium text-yellow-950">
                    {itemType.name}
                  </span>
                  <span className={clsx("shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset", status.className)}>
                    {status.label}
                  </span>
                </span>
                <span className="text-xs text-gray-500">
                  {showUsage && usage
                    ? `${usage.parts} part${usage.parts === 1 ? "" : "s"}${usage.uncategorised ? ` · ${usage.uncategorised} uncategorised` : ""} · `
                    : showUsage
                      ? "No parts · "
                      : ""}
                  {itemType.rules.length} mapping{itemType.rules.length === 1 ? "" : "s"}
                  {itemType.openSuggestions > 0 && ` · ${itemType.openSuggestions} suggested`}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
